import React, { useEffect, useState } from 'react';
import { 
  Activity, 
  Check, 
  X, 
  AlertCircle,
  User,
  Clock,
  Link
} from 'lucide-react';
import { AIResponse, StreamingUpdate } from '../types/api';
import { cn } from '../lib/utils';

interface StreamingProgressProps {
  isStreaming: boolean;
  currentPersonaId: number | null;
  totalPersonas: number;
  completedResponses: AIResponse[];
  latestUpdate?: StreamingUpdate;
}

const StreamingProgress: React.FC<StreamingProgressProps> = ({
  isStreaming,
  currentPersonaId,
  totalPersonas,
  completedResponses,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    if (!isStreaming) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [isStreaming, startTime]);

  const progress = totalPersonas > 0 ? (completedResponses.length / totalPersonas) * 100 : 0;
  const avgTimePerPersona = completedResponses.length > 0 
    ? elapsedTime / completedResponses.length 
    : 0;
  const estimatedRemaining = avgTimePerPersona > 0 
    ? Math.ceil(avgTimePerPersona * (totalPersonas - completedResponses.length))
    : 0;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Calculate response statistics
  const siCount = completedResponses.filter(r => r.classified_answer === 'si').length;
  const noCount = completedResponses.filter(r => r.classified_answer === 'no').length;
  const otherCount = completedResponses.filter(r => r.classified_answer === 'other').length;

  if (!isStreaming && completedResponses.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      {/* Header */}
      <div className={cn(
        "p-4",
        isStreaming ? "bg-foreground text-background" : "bg-muted"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {isStreaming ? (
              <Activity className="h-4 w-4 animate-pulse" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            <div>
              <h3 className="font-medium text-sm">
                {isStreaming ? 'Processing' : 'Complete'}
              </h3>
              <p className={cn(
                "text-xs",
                isStreaming ? "text-background/70" : "text-muted-foreground"
              )}>
                {completedResponses.length} of {totalPersonas} personas
              </p>
            </div>
          </div>
          
          <div className={cn(
            "flex items-center space-x-3 text-xs",
            isStreaming ? "text-background/80" : "text-muted-foreground"
          )}>
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>{formatTime(elapsedTime)}</span>
            </div>
            {isStreaming && estimatedRemaining > 0 && (
              <span>~{formatTime(estimatedRemaining)} left</span>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className={cn(
          "mt-3 h-1 rounded-full overflow-hidden",
          isStreaming ? "bg-background/20" : "bg-border"
        )}>
          <div 
            className={cn(
              "h-full rounded-full transition-all duration-500 ease-out",
              isStreaming ? "bg-background" : "bg-foreground"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-3 grid grid-cols-4 gap-2">
        <div className="text-center p-2 rounded bg-muted/30">
          <div className="text-lg font-semibold">{completedResponses.length}</div>
          <div className="text-[10px] text-muted-foreground flex items-center justify-center space-x-0.5">
            <User className="h-2.5 w-2.5" />
            <span>Done</span>
          </div>
        </div>
        
        <div className="text-center p-2 rounded bg-muted/30">
          <div className="text-lg font-semibold">{siCount}</div>
          <div className="text-[10px] text-muted-foreground flex items-center justify-center space-x-0.5">
            <Check className="h-2.5 w-2.5" />
            <span>Yes</span>
          </div>
        </div>
        
        <div className="text-center p-2 rounded bg-muted/30">
          <div className="text-lg font-semibold">{noCount}</div>
          <div className="text-[10px] text-muted-foreground flex items-center justify-center space-x-0.5">
            <X className="h-2.5 w-2.5" />
            <span>No</span>
          </div>
        </div>
        
        <div className="text-center p-2 rounded bg-muted/30">
          <div className="text-lg font-semibold">{otherCount}</div>
          <div className="text-[10px] text-muted-foreground flex items-center justify-center space-x-0.5">
            <AlertCircle className="h-2.5 w-2.5" />
            <span>Other</span>
          </div>
        </div>
      </div>

      {/* Live Response Feed */}
      {isStreaming && completedResponses.length > 0 && (
        <div className="px-3 pb-3">
          <div className="flex items-center space-x-1.5 mb-2">
            <Activity className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Recent</span>
          </div>
          
          <div className="space-y-1.5 max-h-36 overflow-y-auto">
            {completedResponses.slice(-4).reverse().map((response, idx) => (
              <div 
                key={response.persona_id}
                className={cn(
                  "flex items-center justify-between p-2 rounded border bg-card",
                  "animate-in fade-in duration-200",
                  idx === 0 && "border-foreground/20"
                )}
              >
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                    {response.persona_id + 1}
                  </div>
                  <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                    {response.full_response.slice(0, 40)}...
                  </div>
                </div>
                
                <div className="flex items-center space-x-1.5">
                  {response.reasoning && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                      <Link className="h-2.5 w-2.5 mr-0.5" />
                      CoT
                    </span>
                  )}
                  <span className={cn(
                    "px-1.5 py-0.5 rounded text-xs font-medium",
                    "bg-muted text-foreground"
                  )}>
                    {response.classified_answer}
                  </span>
                  
                  {response.ground_truth && (
                    <span className={cn(
                      "w-5 h-5 flex items-center justify-center rounded text-xs",
                      response.classified_answer === response.ground_truth
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {response.classified_answer === response.ground_truth ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current Processing Indicator */}
      {isStreaming && currentPersonaId !== null && (
        <div className="px-3 pb-3">
          <div className="flex items-center space-x-2 p-2 rounded bg-muted/50 border">
            <div className="h-6 w-6 rounded-full bg-foreground text-background flex items-center justify-center">
              <Activity className="h-3 w-3 animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-medium">
                Persona {currentPersonaId + 1}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Generating response...
              </div>
            </div>
            <div className="flex space-x-0.5">
              <div className="w-1.5 h-1.5 bg-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-1.5 h-1.5 bg-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-1.5 h-1.5 bg-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StreamingProgress;
